import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { createStackScreenContainer } from './screen';
import { createStackRootContainer } from './root';
export const StackOutletContext = React.createContext(null);
export function createStackNavigator() {
    return {
        create(config) {
            const navigator = {
                useControl() {
                    const [searchParams, setSearchParams] = useSearchParams();
                    const STORAGE_KEY = '__stack_navigator_stack';
                    const getFrames = () => {
                        try {
                            const raw = sessionStorage.getItem(STORAGE_KEY);
                            return raw ? JSON.parse(raw) : [];
                        }
                        catch {
                            return [];
                        }
                    };
                    const setFrames = (frames) => {
                        try {
                            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(frames));
                        }
                        catch { }
                    };
                    const push = React.useCallback((update) => {
                        const next = new URLSearchParams(searchParams);
                        const frames = getFrames();
                        for (const [param, value] of Object.entries(update)) {
                            frames.push(param);
                            if (value === undefined) {
                                // no change
                            }
                            else if (value === null) {
                                next.delete(param);
                            }
                            else {
                                next.set(param, String(value));
                            }
                        }
                        setFrames(frames);
                        setSearchParams(next, { replace: false });
                    }, [searchParams, setSearchParams]);
                    const pop = React.useCallback(() => {
                        const next = new URLSearchParams(searchParams);
                        const frames = getFrames();
                        const last = frames.pop();
                        setFrames(frames);
                        if (last)
                            next.delete(last);
                        setSearchParams(next, { replace: false });
                    }, [searchParams, setSearchParams]);
                    const root = React.useCallback(() => {
                        const next = new URLSearchParams(searchParams);
                        const frames = getFrames();
                        // remove all params that were added via stack
                        for (const key of frames) {
                            next.delete(key);
                        }
                        setFrames([]);
                        setSearchParams(next, { replace: false });
                    }, [searchParams, setSearchParams]);
                    return { push, pop, root };
                },
                useParams() {
                    const [searchParams] = useSearchParams();
                    const entries = Object.fromEntries(searchParams.entries());
                    const result = config.schema.safeParse(entries);
                    if (result.success)
                        return result.data;
                    return {};
                },
                Component: {
                    Root: createStackRootContainer(),
                    Route: createStackScreenContainer(),
                    Outlet() {
                        const content = React.useContext(StackOutletContext);
                        return _jsx(_Fragment, { children: content });
                    },
                    BackButton: React.forwardRef(({ as: Button, ...buttonProps }, ref) => {
                        const control = navigator.useControl();
                        const onClick = React.useCallback(() => {
                            control.pop();
                        }, [control]);
                        return (_jsx(Button, { ref: ref, ...buttonProps, onClick: onClick }));
                    }),
                },
            };
            return navigator;
        },
    };
}
//# sourceMappingURL=stackNavigatorProvider.js.map